import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
  MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import { PageHeader, EmptyState } from "../../components/ui";
import { listWorkCenters } from "../../api/workCenters";
import { listShifts } from "../../api/shifts";
import { listDowntimeReasons } from "../../api/downtimeReasons";
import {
  completeProductionOrderOperation,
  getProductionOrder,
  getProductionOrderOperationRequirements,
  listAssignedProductionOrders,
} from "../../api/productionOrders";
import {
  correctShiftProductionLot,
  createShiftProductionEntry,
  listShiftEntryOperators,
  listShiftProductionEntries,
  reverseBaskiProductionLot,
} from "../../api/shiftProduction";
import { reverseOperationApproval } from "../../api/reversal";
import { formatReservationLotNo, splitReservationLotNo } from "../../utils/reservationLot";

const emptyProductionRows = () => Array.from({ length: 20 }, () => ({ optionKey: "", qty: "", scrapQty: "" }));
const emptyConsumptionRows = () => Array.from({ length: 20 }, () => ({ optionKey: "" }));
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" });
function createSubmissionUuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
const WORK_CENTER_GROUPS = [
  { key: "BSK", label: "Baskı", color: "#fff4b8", border: "#e4c542", match: ["BSK", "BASKI"] },
  { key: "KSM", label: "Kesim", color: "#dcecff", border: "#6ea8f7", match: ["KSM", "KES", "CUT"] },
  { key: "AYK", label: "Ayıklama", color: "#ffdede", border: "#ef8888", match: ["AYK", "AYIK"] },
  { key: "FRM", label: "Formlama", color: "#dcf5df", border: "#79c982", match: ["FRM", "FORM"] },
  { key: "SHRK", label: "Shrink", color: "#ffe8cc", border: "#efa65c", match: ["SHRK", "SHRINK", "PAKET"] },
  { key: "KOLI", label: "Kolileme", color: "#eadcff", border: "#a978df", match: ["KOLI", "KOLİ", "KOLILE"] },
];
const initialForm = () => ({
  production_date: today(), shift_id: "", operator_user_ids: [], note: "",
  machine_speed: "", downtimes: [],
});

function errorMessage(error, fallback) {
  return error?.response?.data?.error?.message || error?.message || fallback;
}

function opBelongsToCenter(op, workCenterId) {
  const id = Number(workCenterId);
  const planned = String(op?.planned_work_center_ids || "")
    .split(",").map(Number).filter(Number.isFinite);
  return planned.includes(id) || Number(op?.planned_work_center_id) === id || Number(op?.work_center_id) === id;
}

function workCenterGroupKey(workCenter) {
  const value = [workCenter?.group_code, workCenter?.group_name, workCenter?.code, workCenter?.name]
    .filter(Boolean).join(" ").toLocaleUpperCase("tr-TR");
  return WORK_CENTER_GROUPS.find((group) => group.match.some((token) => value.includes(token)))?.key || "OTHER";
}

function operationUnit(code) {
  const value = String(code || "").toLowerCase();
  if (/kes|cut/.test(value)) return "tabaka";
  if (/bask|print/.test(value)) return "metre/adet";
  if (/shrink|şirink/.test(value)) return "paket";
  if (/koli/.test(value)) return "koli";
  return "adet";
}

function supportsLotScrap(code) {
  const value = String(code || "").trim().toLowerCase();
  return /bask|print|kesim|cut|form|shrink|şirink/.test(value)
    || ["bsk", "prn", "ksm", "frm", "shrk"].includes(value);
}

function lotScrapUnit(code) {
  const value = String(code || "").trim().toLowerCase();
  if (/bask|print/.test(value) || ["bsk", "prn"].includes(value)) return "metre";
  if (/kesim|cut/.test(value) || value === "ksm") return "tabaka";
  if (/form|shrink|şirink/.test(value) || ["frm", "shrk"].includes(value)) return "adet";
  return "fire kullanılmıyor";
}

function machineSpeedUnit(code) {
  const value = String(code || "").toLowerCase();
  if (/bask|print/.test(value)) return "mt/dk";
  if (/kes|cut/.test(value)) return "tabaka/dk";
  if (/ayik|ayıkl|strip/.test(value)) return "plaka/dk";
  if (/shrink|şirink/.test(value)) return "paket/dk";
  if (/koli/.test(value)) return "koli/dk";
  return "adet/dk";
}

function cuttingUsesSheetInput(op) {
  const plates = Number(op?.planned_knife_plates_count);
  return /kesim|cut/i.test(String(op?.operation_code || "")) && Number.isFinite(plates) && plates > 0 && op?.queue_sheet_target != null;
}

function requirementsQty(op, displayedQty) {
  const qty = Number(displayedQty);
  if (!Number.isFinite(qty) || qty <= 0) return qty;
  return cuttingUsesSheetInput(op) ? Math.round(qty * Number(op.planned_knife_plates_count)) : qty;
}

function formatDateShort(value) {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : "—";
}

function formatTimeShort(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString("tr-TR", { timeZone: "Europe/Istanbul", hour: "2-digit", minute: "2-digit" });
}

function integerValue(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number).toLocaleString("tr-TR") : "—";
}

export default function ShiftProductionEntryPage() {
  const [workCenters, setWorkCenters] = useState([]);
  const [workCenterSearch, setWorkCenterSearch] = useState("");
  const [activeGroupKey, setActiveGroupKey] = useState("BSK");
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [operationChoices, setOperationChoices] = useState([]);
  // Parti bazında ters alma penceresi
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseBusy, setReverseBusy] = useState(false);
  const [lotDrafts, setLotDrafts] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [downtimeReasons, setDowntimeReasons] = useState([]);
  const [operators, setOperators] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [requirements, setRequirements] = useState([]);
  const [availableLots, setAvailableLots] = useState([]);
  const [primaryInputError, setPrimaryInputError] = useState("");
  const [productionRows, setProductionRows] = useState(emptyProductionRows);
  const [consumptionRows, setConsumptionRows] = useState(emptyConsumptionRows);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [entries, setEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({ date_from: today(), date_to: today(), shift_id: "", operator_user_id: "", work_center_id: "" });
  const submissionRef = useRef(null);
  const submitInFlightRef = useRef(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setEntries(await listShiftProductionEntries({
        ...historyFilters,
        work_center_id: historyFilters.work_center_id || selectedCenter?.id || undefined,
      }));
    } catch (err) {
      setError(errorMessage(err, "Vardiya geçmişi yüklenemedi."));
    } finally { setHistoryLoading(false); }
  }, [historyFilters, selectedCenter?.id]);

  useEffect(() => {
    Promise.all([listWorkCenters(), listShifts(), listDowntimeReasons()])
      .then(([centers, shiftRows, reasonRows]) => {
        setWorkCenters(centers || []);
        const firstAvailableGroup = WORK_CENTER_GROUPS.find((group) =>
          (centers || []).some((workCenter) => workCenterGroupKey(workCenter) === group.key)
        );
        if (firstAvailableGroup) setActiveGroupKey(firstAvailableGroup.key);
        setShifts(shiftRows || []);
        setDowntimeReasons(reasonRows || []);
      })
      .catch((err) => setError(errorMessage(err, "Başlangıç verileri yüklenemedi.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    if (!selectedCenter) { setOrders([]); setOperators([]); return; }
    setOrdersLoading(true);
    Promise.all([
      listAssignedProductionOrders(selectedCenter.id),
      listShiftEntryOperators(selectedCenter.id),
    ])
      .then(([orderRows, operatorRows]) => { setOrders(orderRows || []); setOperators(operatorRows || []); })
      .catch((err) => setError(errorMessage(err, "Planlanan işler yüklenemedi.")))
      .finally(() => setOrdersLoading(false));
  }, [selectedCenter]);

  const filteredCenters = useMemo(() => {
    const term = workCenterSearch.trim().toLocaleLowerCase("tr-TR");
    return !term ? workCenters : workCenters.filter((wc) =>
      `${wc.code || ""} ${wc.name || ""}`.toLocaleLowerCase("tr-TR").includes(term));
  }, [workCenters, workCenterSearch]);

  const visibleGroup = useMemo(
    () => WORK_CENTER_GROUPS.find((group) => group.key === activeGroupKey) || WORK_CENTER_GROUPS[0],
    [activeGroupKey]
  );

  const visibleGroupCenters = useMemo(
    () => filteredCenters.filter((workCenter) => workCenterGroupKey(workCenter) === visibleGroup.key),
    [filteredCenters, visibleGroup.key]
  );

  const lotOptions = useMemo(() => {
    const reqByMaterial = new Map(requirements.map((req) => [Number(req.material_id), req]));
    const result = [];
    for (const item of availableLots || []) {
      const req = reqByMaterial.get(Number(item.material_id)) || {};
      for (const lot of item.lots || []) {
        const variantId = lot.material_variant_id ?? item.material_variant_id ?? null;
        const key = `${item.material_id}:${variantId || 0}:${lot.lot_no}`;
        const inputRole = item.input_role || req.input_role || "auxiliary";
        const displayUom = /kesim|cut/i.test(String(selectedOperation?.operation_code || "")) && inputRole === "primary"
          ? "metre"
          : item.uom || req.uom || "adet";
        result.push({
          key,
          material_id: Number(item.material_id),
          material_variant_id: variantId,
          lot_no: lot.lot_no,
          on_hand_qty: Number(lot.on_hand_qty || 0),
          uom: item.uom || req.uom || "adet",
          input_role: inputRole,
          label: `${req.material_code || `Malzeme ${item.material_id}`} — ${req.material_name || ""} | Parti ${formatReservationLotNo(lot.lot_no)}${lot.material_variant_display_name ? ` | ${lot.material_variant_display_name}` : ""} | Stok ${Number(lot.on_hand_qty || 0).toLocaleString("tr-TR")} ${displayUom}`,
        });
      }
    }
    return result;
  }, [requirements, availableLots, selectedOperation?.operation_code]);

  const primaryLotOptions = useMemo(() => lotOptions.filter((item) => item.input_role === "primary"), [lotOptions]);
  const auxiliaryLotOptions = useMemo(() => lotOptions.filter((item) => item.input_role !== "primary"), [lotOptions]);
  const auxiliaryRequirements = useMemo(
    () => requirements.filter((item) => {
      const rawType = String(item.raw_material_type || "").toLowerCase();
      const isInkLak = ["ink", "lak", "lak_flekso", "flekso_lak", "flexo_lak"].includes(rawType);
      return item.input_role !== "primary" && (Number(item.qty || 0) > 0 || isInkLak);
    }),
    [requirements]
  );

  async function chooseOrder(order) {
    setOrdersLoading(true);
    try {
      const detail = await getProductionOrder(order.id);
      const ops = (detail?.production_order_operations || []).filter((op) =>
        String(op.status) !== "finished" && opBelongsToCenter(op, selectedCenter.id));
      if (!ops.length) throw new Error("Bu iş merkezinde açık planlı operasyon bulunamadı.");
      setSelectedOrder(detail);
      if (ops.length === 1) await openEntry(detail, ops[0]);
      else setOperationChoices(ops);
    } catch (err) { setError(errorMessage(err, "İş emri açılamadı.")); }
    finally { setOrdersLoading(false); }
  }

  async function openEntry(order, op) {
    submissionRef.current = null;
    submitInFlightRef.current = false;
    setOperationChoices([]);
    setSelectedOrder(order);
    setSelectedOperation(op);
    const target = cuttingUsesSheetInput(op) ? Number(op.queue_sheet_target || 0) : Number(op.qty_target || 0);
    const remaining = Math.max(0, target - Number(op.qty_done || 0));
    const nextForm = initialForm();
    setForm(nextForm);
    setProductionRows(emptyProductionRows());
    setConsumptionRows(emptyConsumptionRows());
    setRequirements([]);
    setAvailableLots([]);
    setPrimaryInputError("");
    setFormError("");
    setDialogOpen(true);
    const requirementsRemaining = remaining > 0 ? remaining : (/kesim|cut/i.test(String(op?.operation_code || "")) ? 1 : 0);
    if (requirementsRemaining > 0) await loadRequirements(order.id, op.routing_operation_id, requirementsRemaining, op);
  }

  async function loadRequirements(orderId = selectedOrder?.id, operationId = selectedOperation?.routing_operation_id, qtyValue = null, operation = selectedOperation) {
    const rawFallbackQty = Number(operation?.qty_target || 0) - Number(operation?.qty_done || 0);
    const fallbackQty = rawFallbackQty > 0 ? rawFallbackQty : (/kesim|cut/i.test(String(operation?.operation_code || "")) ? 1 : rawFallbackQty);
    const qty = Number(qtyValue || fallbackQty);
    if (!orderId || !operationId || !Number.isFinite(qty) || qty <= 0) return;
    setFormLoading(true);
    setFormError("");
    try {
      const data = await getProductionOrderOperationRequirements(orderId, operationId, requirementsQty(operation || {}, qty));
      setRequirements(data?.requirements || []);
      setAvailableLots(data?.available_lots || []);
      setPrimaryInputError(data?.primary_input_error || "");
      setProductionRows(emptyProductionRows());
      setConsumptionRows(emptyConsumptionRows());
    } catch (err) { setFormError(errorMessage(err, "Tüketilecek malzemeler yüklenemedi.")); }
    finally { setFormLoading(false); }
  }

  function updateConsumption(index, patch) {
    setConsumptionRows((prev) => prev.map((row, i) => i === index ? { ...row, ...patch } : row));
  }

  function updateProduction(index, patch) {
    setProductionRows((prev) => prev.map((row, i) => i === index ? { ...row, ...patch } : row));
  }

  function addDowntime() {
    setForm((prev) => ({ ...prev, downtimes: [...(prev.downtimes || []), { reason_id: "", duration_minutes: "" }] }));
  }

  function updateDowntime(index, patch) {
    setForm((prev) => ({ ...prev, downtimes: (prev.downtimes || []).map((row, i) => i === index ? { ...row, ...patch } : row) }));
  }

  function removeDowntime(index) {
    setForm((prev) => ({ ...prev, downtimes: (prev.downtimes || []).filter((_, i) => i !== index) }));
  }

  const opCode = String(selectedOperation?.operation_code || "");
  async function submitEntry() {
    setFormError("");
    const filledProductionRows = productionRows.filter((row) => row.optionKey || String(row.qty).trim() || String(row.scrapQty).trim());
    if (!form.shift_id || !form.operator_user_ids.length || !filledProductionRows.length) {
      setFormError("Tarih, vardiya, en az bir operatör ve en az bir parti üretim miktarı zorunludur.");
      return;
    }
    if (primaryInputError) { setFormError(primaryInputError); return; }
    const productionLots = [];
    let qty = 0;
    const isBaskiEntry = /bask|print/i.test(opCode);
    const isCuttingEntry = /kesim|cut/i.test(opCode);
    const isFormlamaEntry = /frm|form/i.test(opCode);
    const isShrinkEntry = /shrk|shrink|şirink/i.test(opCode);
    const requiresLotScrap = isBaskiEntry || isCuttingEntry || isFormlamaEntry || isShrinkEntry;
    let hasBaskiReservedLot = false;
    let baskiScrap = 0;
    let cuttingScrap = 0;
    let formlamaScrap = 0;
    let shrinkScrap = 0;
    for (const row of productionRows) {
      if (!row.optionKey && !String(row.qty).trim() && !String(row.scrapQty).trim()) continue;
      if (!row.optionKey || !Number.isFinite(Number(row.qty)) || Number(row.qty) <= 0) {
        setFormError("Ana girdi satırlarında parti ve pozitif üretilen miktar birlikte girilmelidir.");
        return;
      }
      const option = primaryLotOptions.find((item) => item.key === row.optionKey);
      if (!option) { setFormError("Seçilen partilerden biri artık kullanılamıyor."); return; }
      if (isBaskiEntry) {
        if (!String(row.scrapQty).trim() || !Number.isFinite(Number(row.scrapQty)) || Number(row.scrapQty) < 0) {
          setFormError("Her baskı partisinde fire miktarı girilmelidir; fire yoksa 0 yazın.");
          return;
        }
        const reservationNo = splitReservationLotNo(option.lot_no).reservationNo;
        hasBaskiReservedLot = hasBaskiReservedLot || !!reservationNo;
        const consumedQty = Number(row.qty) + Number(row.scrapQty);
        if (consumedQty > Number(option.on_hand_qty) + 0.0001) {
          setFormError(`Üretim + fire ${formatReservationLotNo(option.lot_no)} stok bakiyesini aşamaz.`);
          return;
        }
        baskiScrap += Number(row.scrapQty);
      } else if (isCuttingEntry) {
        if (!String(row.scrapQty).trim() || !Number.isInteger(Number(row.scrapQty)) || Number(row.scrapQty) < 0) {
          setFormError("Her kesim partisinde fire miktarı tam sayı tabaka olarak girilmelidir; fire yoksa 0 yazın.");
          return;
        }
        cuttingScrap += Number(row.scrapQty);
      } else if (isFormlamaEntry || isShrinkEntry) {
        if (!String(row.scrapQty).trim() || !Number.isInteger(Number(row.scrapQty)) || Number(row.scrapQty) < 0) {
          setFormError(`Her ${isFormlamaEntry ? "formlama" : "shrink"} partisinde fire miktarı tam sayı adet olarak girilmelidir; fire yoksa 0 yazın.`);
          return;
        }
        if (isFormlamaEntry) formlamaScrap += Number(row.scrapQty);
        if (isShrinkEntry) shrinkScrap += Number(row.scrapQty);
      }
      productionLots.push({
        material_id: option.material_id, material_variant_id: option.material_variant_id,
        lot_no: option.lot_no, produced_qty: Number(row.qty),
        ...(requiresLotScrap ? { scrap_qty: Number(row.scrapQty) } : {}),
      });
      qty += Number(row.qty);
    }
    if (!Number.isFinite(qty) || qty <= 0) { setFormError("Toplam üretilen tabaka miktarı pozitif olmalıdır."); return; }
    const lotSelections = [];
    for (const row of consumptionRows) {
      if (!row.optionKey) continue;
      const option = auxiliaryLotOptions.find((item) => item.key === row.optionKey);
      if (!option) { setFormError("Seçilen yardımcı malzeme partilerinden biri artık kullanılamıyor."); return; }
      lotSelections.push({
        material_id: option.material_id, material_variant_id: option.material_variant_id, lot_no: option.lot_no,
      });
    }
    const missingAuxiliary = auxiliaryRequirements.find((req) => !lotSelections.some((row) =>
      Number(row.material_id) === Number(req.material_id) &&
      (req.material_variant_id == null || Number(row.material_variant_id) === Number(req.material_variant_id))
    ));
    if (missingAuxiliary) {
      setFormError(`${missingAuxiliary.material_name || missingAuxiliary.material_code || missingAuxiliary.material_id} için tüketilecek parti seçilmelidir.`);
      return;
    }
    if (!Number.isFinite(Number(form.machine_speed)) || Number(form.machine_speed) <= 0) {
      setFormError(`Makine hızı pozitif olmalıdır (${machineSpeedUnit(opCode)}).`);
      return;
    }
    const downtimeRows = form.downtimes || [];
    for (const row of downtimeRows) {
      if (!row.reason_id || !Number.isFinite(Number(row.duration_minutes)) || Number(row.duration_minutes) <= 0) {
        setFormError("Her duruş satırında sebep ve pozitif süre birlikte girilmelidir.");
        return;
      }
    }
    const baskiFireConfirmation = isBaskiEntry
      ? `\nGirilen baskı firesi: ${baskiScrap.toLocaleString("tr-TR")} metre${hasBaskiReservedLot ? "\nSeçilen rezervasyonlu bobinler onaylanmış sayılacak; kullanılmayan stok korunacaktır." : ""}`
      : "";
    const cuttingFireConfirmation = isCuttingEntry
      ? `\nGirilen kesim firesi: ${cuttingScrap.toLocaleString("tr-TR")} tabaka`
      : "";
    const formlamaFireConfirmation = isFormlamaEntry
      ? `\nGirilen formlama firesi: ${formlamaScrap.toLocaleString("tr-TR")} adet`
      : "";
    const shrinkFireConfirmation = isShrinkEntry
      ? `\nGirilen shrink firesi: ${shrinkScrap.toLocaleString("tr-TR")} adet`
      : "";
    if (!window.confirm(`${form.production_date} tarihli vardiya üretimi kaydedilsin mi?\nToplam üretim: ${qty.toLocaleString("tr-TR")}${baskiFireConfirmation}${cuttingFireConfirmation}${formlamaFireConfirmation}${shrinkFireConfirmation}\nEkip: ${form.operator_user_ids.length} operatör\nAna girdi partisi: ${productionLots.length}\nYardımcı tüketim partisi: ${lotSelections.length}\nTüketim miktarları operasyon kurallarıyla otomatik hesaplanacaktır.`)) return;
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setFormLoading(true);
    try {
      const payload = {
        work_center_id: Number(selectedCenter.id), production_date: form.production_date,
        shift_id: Number(form.shift_id), operator_user_ids: form.operator_user_ids.map(Number),
        qty_done: qty, note: form.note.trim() || null,
        production_lots: productionLots, consumption_lots: lotSelections,
        downtimes: (form.downtimes || []).map((row) => ({ reason_id: Number(row.reason_id), duration_minutes: Number(row.duration_minutes) })),
      };
      payload.machine_speed = Number(form.machine_speed);
      const fingerprint = JSON.stringify(payload);
      if (!submissionRef.current || submissionRef.current.fingerprint !== fingerprint) {
        submissionRef.current = { fingerprint, postingGroupId: createSubmissionUuid() };
      }
      payload.posting_group_id = submissionRef.current.postingGroupId;
      await createShiftProductionEntry(selectedOrder.id, selectedOperation.routing_operation_id, payload);
      submissionRef.current = null;
      setDialogOpen(false);
      setError("");
      await Promise.all([listAssignedProductionOrders(selectedCenter.id).then(setOrders), loadHistory()]);
    } catch (err) { setFormError(errorMessage(err, "Vardiya üretimi kaydedilemedi.")); }
    finally { submitInFlightRef.current = false; setFormLoading(false); }
  }

  async function completeSelectedOperation() {
    if (!selectedOrder?.id || !selectedOperation?.routing_operation_id || !selectedCenter?.id) return;
    const confirmed = window.confirm(
      "Buna onay verirseniz bu operasyon komple bitmiş olarak işaretlenecektir. Onaylıyor musunuz?"
    );
    if (!confirmed) return;
    setFormError("");
    setFormLoading(true);
    try {
      await completeProductionOrderOperation(
        selectedOrder.id,
        selectedOperation.routing_operation_id,
        null,
        selectedCenter.id
      );
      setDialogOpen(false);
      setError("");
      await Promise.all([
        listAssignedProductionOrders(selectedCenter.id).then(setOrders),
        loadHistory(),
      ]);
    } catch (err) {
      setFormError(errorMessage(err, "Operasyon komple kapatılamadı."));
    } finally {
      setFormLoading(false);
    }
  }

  // Ters alma artık pencere açar: operatör hangi partileri geri alacağını seçer.
  function reverseEntry(entry) {
    setReverseTarget(entry);
    setReverseReason("");
    setLotDrafts(Object.fromEntries((entry.production_lots || []).map((lot) => [
      Number(lot.allocation_id),
      { produced_qty: String(Number(lot.produced_qty || 0)), scrap_qty: String(Number(lot.scrap_qty || 0)) },
    ])));
  }

  const canReverseByLot = !!reverseTarget?.can_reverse_by_lot
    && (reverseTarget?.production_lots || []).some((lot) => !lot.reversed_at && lot.reversible);
  const isBaskiReverseTarget = /bask|print|\bbsk\b|\bprn\b/i.test(String(reverseTarget?.operation_code || ""));

  async function submitLotReversal(lot) {
    if (!reverseTarget) return;
    const reason = reverseReason.trim();
    if (!reason) {
      setError("Ters alma nedeni zorunludur.");
      return;
    }
    setReverseBusy(true);
    try {
      await reverseOperationApproval(
        reverseTarget.approval_id,
        reason,
        [lot.allocation_id]
      );
      setReverseTarget(null);
      setReverseReason("");
      await loadHistory();
    } catch (err) {
      setError(errorMessage(err, "Ters alma başarısız."));
    } finally {
      setReverseBusy(false);
    }
  }

  function updateLotDraft(allocationId, field, value) {
    setLotDrafts((current) => ({
      ...current,
      [allocationId]: { ...current[allocationId], [field]: value },
    }));
  }

  async function submitLotCorrection(lot) {
    const reason = reverseReason.trim();
    if (!reason) {
      setError("Düzeltme nedeni zorunludur.");
      return;
    }
    const draft = lotDrafts[lot.allocation_id] || {};
    setReverseBusy(true);
    try {
      await correctShiftProductionLot(reverseTarget.approval_id, lot.allocation_id, {
        produced_qty: Number(draft.produced_qty),
        scrap_qty: Number(draft.scrap_qty),
        expected_produced_qty: Number(lot.produced_qty),
        expected_scrap_qty: Number(lot.scrap_qty || 0),
        reason,
      });
      setReverseTarget(null);
      setReverseReason("");
      await loadHistory();
    } catch (err) {
      setError(errorMessage(err, "Parti düzeltilemedi."));
    } finally {
      setReverseBusy(false);
    }
  }

  async function submitBaskiLotReversal(lot) {
    const reason = reverseReason.trim();
    if (!reason) {
      setError("Geri alma nedeni zorunludur.");
      return;
    }
    setReverseBusy(true);
    try {
      await reverseBaskiProductionLot(reverseTarget.approval_id, lot.allocation_id, reason);
      setReverseTarget(null);
      setReverseReason("");
      await loadHistory();
    } catch (err) {
      setError(errorMessage(err, "Parti onayı geri alınamadı."));
    } finally {
      setReverseBusy(false);
    }
  }

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <Stack spacing={3}>
      <PageHeader title="Vardiya Üretim Girişi" subtitle="İş merkezi ve vardiya bazında ekip üretimi ile gerçek tüketimleri toplu kaydedin." />
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800} mb={1.5}>1. İş merkezi seçin</Typography>
        <TextField fullWidth size="small" label="İş merkezi ara" value={workCenterSearch} onChange={(e) => setWorkCenterSearch(e.target.value)} sx={{ mb: 2 }} />
        <Box sx={{ overflowX: "auto", pb: 0.5 }}>
          <Box display="grid" gridTemplateColumns={{ xs: "repeat(6, minmax(112px, 1fr))", md: "repeat(6, minmax(0, 1fr))" }} gap={1} minWidth={{ xs: 700, md: 0 }}>
            {WORK_CENTER_GROUPS.map((group) => {
              const count = filteredCenters.filter((wc) => workCenterGroupKey(wc) === group.key).length;
              const active = visibleGroup.key === group.key;
              return (
                <Card
                  key={group.key}
                  variant="outlined"
                  sx={{
                    backgroundColor: group.color,
                    borderColor: active ? group.border : "rgba(0,0,0,0.12)",
                    borderWidth: active ? 2 : 1,
                    boxShadow: active ? `0 0 0 1px ${group.border}` : "none",
                  }}
                >
                  <CardActionArea onClick={() => { setActiveGroupKey(group.key); setSelectedCenter(null); }} sx={{ minHeight: 66 }}>
                    <CardContent sx={{ py: 1, px: 1.25, "&:last-child": { pb: 1 } }}>
                      <Typography fontWeight={900} noWrap>{group.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{count} iş merkezi</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2} mb={1}>
          <Typography fontWeight={900}>{visibleGroup.label} makineleri</Typography>
          <Typography variant="body2" color="text.secondary">{visibleGroupCenters.length} iş merkezi</Typography>
        </Stack>
        {!visibleGroupCenters.length ? <EmptyState description="Bu grupta aramanıza uyan iş merkezi yok." /> : (
          <Box display="grid" gridTemplateColumns={{ xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))", lg: "repeat(8, minmax(0, 1fr))" }} gap={1}>
            {visibleGroupCenters.map((wc) => {
              const selected = selectedCenter?.id === wc.id;
              return (
                <Card key={wc.id} variant="outlined" sx={{ minWidth: 0, borderColor: selected ? "primary.main" : "rgba(0,0,0,0.12)", borderWidth: selected ? 2 : 1 }}>
                  <CardActionArea onClick={() => { setActiveGroupKey(workCenterGroupKey(wc)); setSelectedCenter(wc); }} sx={{ minHeight: 68 }}>
                    <CardContent sx={{ py: 1, px: 1.1, "&:last-child": { pb: 1 } }}>
                      <Typography fontWeight={900} noWrap title={wc.code}>{wc.code}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap title={wc.name}>{wc.name}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        )}
      </Paper>

      {selectedCenter ? <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box><Typography variant="h6" fontWeight={800}>2. Planlanan iş emrini seçin</Typography><Typography variant="body2" color="text.secondary">{selectedCenter.code} · {selectedCenter.name}</Typography></Box>
          {ordersLoading ? <CircularProgress size={24} /> : null}
        </Stack>
        {!orders.length && !ordersLoading ? <EmptyState description="Bu iş merkezinde açık planlı operasyon yok." /> : null}
        {orders.length ? <Box sx={{ overflowX: "auto", border: "1px solid #dbe3f0", borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 860, bgcolor: "#fff", "& .MuiTableCell-head": { bgcolor: "#eef2ff", color: "#283593", fontWeight: 900, whiteSpace: "nowrap" }, "& .MuiTableCell-root": { borderColor: "#e5e7eb", py: 1.1 }, "& .MuiTableBody-root .MuiTableRow-root:hover": { bgcolor: "#f8faff" } }}>
            <TableHead><TableRow>
              <TableCell>İş emri no</TableCell>
              <TableCell>Müşteri adı</TableCell>
              <TableCell>Gramaj</TableCell>
              <TableCell>Ölçü</TableCell>
              <TableCell>Kalıp tipi</TableCell>
              <TableCell>Renk</TableCell>
              <TableCell>Paket içi adet</TableCell>
            </TableRow></TableHead>
            <TableBody>{orders.map((order) => {
              const diameter = order.plate_diameter_cm ?? order.diameter_cm;
              const moldType = order.kalip_tipi || order.mold_type;
              const color = order.renk || order.print_colors;
              return <TableRow key={order.id} hover onClick={() => chooseOrder(order)} sx={{ cursor: "pointer" }}>
                <TableCell><Typography fontWeight={900} color="primary.main">#{order.order_no || "—"}</Typography></TableCell>
                <TableCell>{order.customer_name || "—"}</TableCell>
                <TableCell>{order.gramaj != null && order.gramaj !== "" ? `${integerValue(order.gramaj)} gr` : "—"}</TableCell>
                <TableCell>{diameter != null && diameter !== "" ? `${integerValue(diameter)} cm` : "—"}</TableCell>
                <TableCell>{moldType || "—"}</TableCell>
                <TableCell>{color || "—"}</TableCell>
                <TableCell>{order.paket_ici_adet != null && order.paket_ici_adet !== "" ? integerValue(order.paket_ici_adet) : "—"}</TableCell>
              </TableRow>;
            })}</TableBody>
          </Table>
        </Box> : null}
      </Paper> : null}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800} mb={2}>Vardiya giriş geçmişi</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={2}>
          <TextField type="date" size="small" label="Başlangıç" InputLabelProps={{ shrink: true }} value={historyFilters.date_from} onChange={(e) => setHistoryFilters((p) => ({ ...p, date_from: e.target.value }))} />
          <TextField type="date" size="small" label="Bitiş" InputLabelProps={{ shrink: true }} value={historyFilters.date_to} onChange={(e) => setHistoryFilters((p) => ({ ...p, date_to: e.target.value }))} />
          <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Vardiya</InputLabel><Select label="Vardiya" value={historyFilters.shift_id} onChange={(e) => setHistoryFilters((p) => ({ ...p, shift_id: e.target.value }))}><MenuItem value="">Tümü</MenuItem>{shifts.map((s) => <MenuItem key={s.id} value={s.id}>{s.shift_no}. Vardiya</MenuItem>)}</Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}><InputLabel>Operatör</InputLabel><Select label="Operatör" value={historyFilters.operator_user_id} onChange={(e) => setHistoryFilters((p) => ({ ...p, operator_user_id: e.target.value }))}><MenuItem value="">Tümü</MenuItem>{operators.map((op) => <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>)}</Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}><InputLabel>Makine</InputLabel><Select label="Makine" value={historyFilters.work_center_id} onChange={(e) => setHistoryFilters((p) => ({ ...p, work_center_id: e.target.value }))}><MenuItem value="">Tümü</MenuItem>{workCenters.map((wc) => <MenuItem key={wc.id} value={wc.id}>{wc.code ? `${wc.code} · ` : ""}{wc.name}</MenuItem>)}</Select></FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" onClick={loadHistory}>Yenile</Button>
        </Stack>
        {historyLoading ? <CircularProgress size={24} /> : <Box overflow="auto"><Table size="small"><TableHead><TableRow>
          <TableCell>Tarih / Vardiya</TableCell><TableCell>İş merkezi</TableCell><TableCell>Sipariş</TableCell><TableCell>İş emri</TableCell><TableCell>Ekip</TableCell><TableCell align="right">Üretim</TableCell><TableCell>Parti dağılımı</TableCell><TableCell>Kalite</TableCell><TableCell>Giren</TableCell><TableCell align="right">İşlem</TableCell>
        </TableRow></TableHead><TableBody>{entries.map((entry) => <TableRow key={entry.approval_id} hover>
          <TableCell>{formatDateShort(entry.production_date)} {formatTimeShort(entry.shift_started_at)}<br /><Typography variant="caption">{entry.shift_no_snapshot}. Vardiya</Typography></TableCell>
          <TableCell>{entry.work_center_code}<br />{entry.work_center_name}</TableCell>
          <TableCell>{entry.sales_order_no || "—"}</TableCell>
          <TableCell>{entry.order_no || "—"}</TableCell>
          <TableCell>{entry.operators.map((op) => op.name).join(", ") || "—"}</TableCell>
          <TableCell align="right"><b>{entry.approved_qty.toLocaleString("tr-TR")}</b> {operationUnit(entry.operation_code)}<br /><Typography variant="caption">{entry.consumption_count} tüketim</Typography></TableCell>
          <TableCell>{entry.production_lots?.length ? entry.production_lots.map((lot) => {
            const displayLot = splitReservationLotNo(lot.lot_no);
            const lotScrap = /bask|print|kesim|cut|frm|form|shrk|shrink|şirink/i.test(String(entry.operation_code || ""))
              ? Number(lot.scrap_qty || 0)
              : null;
            const scrapUnit = /kesim|cut/i.test(String(entry.operation_code || ""))
              ? " tabaka"
              : /frm|form|shrk|shrink|şirink/i.test(String(entry.operation_code || "")) ? " adet" : " metre";
            return <Typography key={`${lot.material_id}:${lot.material_variant_id || 0}:${lot.lot_no}`} variant="caption" display="block" sx={lot.reversed_at ? { textDecoration: "line-through", opacity: 0.6 } : undefined}>{displayLot.lotNo}: <b>{Number(lot.produced_qty).toLocaleString("tr-TR")}</b>{lotScrap !== null ? ` · Fire: ${lotScrap.toLocaleString("tr-TR")}${scrapUnit}` : ""}</Typography>;
          }) : "—"}</TableCell>
          <TableCell><Chip size="small" color={entry.status === "reversed" ? "default" : entry.has_partial_reversal ? "warning" : entry.qc_status === "approved" ? "success" : "warning"} label={entry.status === "reversed" ? "Ters alındı" : entry.has_partial_reversal ? "Kısmi ters alındı" : entry.qc_status || "Kalite gerekmiyor"} /></TableCell>
          <TableCell>{entry.entered_by_name || "—"}</TableCell>
          <TableCell align="right">{entry.status !== "reversed" ? <Button size="small" color="primary" onClick={() => reverseEntry(entry)}>Partileri Yönet</Button> : "—"}</TableCell>
        </TableRow>)}</TableBody></Table></Box>}
      </Paper>

      {/* Parti bazında ters alma: yalnızca seçili partilerin onayı geri alınır */}
      <Dialog open={!!reverseTarget} onClose={() => (reverseBusy ? null : setReverseTarget(null))} fullWidth maxWidth="md">
        <DialogTitle>
          {isBaskiReverseTarget ? "Baskı Parti Onayları" : "Parti Onayları"} — #{reverseTarget?.order_no} · OP {reverseTarget?.op_no} {reverseTarget?.operation_code}
          <Typography variant="body2" color="text.secondary">
            {reverseTarget?.production_date} · {reverseTarget?.shift_no_snapshot}. Vardiya · {reverseTarget?.work_center_name}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {!canReverseByLot && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Bu kayıt parti bazında geri alınamaz (eski kayıt veya stok hareketi bağlantısı eksik).
            </Alert>
          )}
          <TextField
            label="İşlem nedeni"
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <Alert severity="info" sx={{ mb: 2 }}>
            Her işlem yalnızca ilgili partiye uygulanır. Düzenleme partiyi onaylı bırakır; “Parti Onayını Geri Al” yalnızca o parti onayını geri alır.
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Parti</TableCell>
                <TableCell align="right">Üretilen</TableCell>
                <TableCell align="right">Fire</TableCell>
                <TableCell>Durum</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(reverseTarget?.production_lots || []).map((lot) => {
                const display = splitReservationLotNo(lot.lot_no);
                const alreadyReversed = !!lot.reversed_at;
                const scrapEnabled = supportsLotScrap(reverseTarget?.operation_code);
                return (
                  <TableRow key={lot.allocation_id} sx={{ opacity: alreadyReversed ? 0.5 : 1 }}>
                    <TableCell>
                      {display.lotNo}
                      {display.reservationNo ? (
                        <Typography variant="caption" color="text.secondary"> · Rez No: {display.reservationNo}</Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {lot.material_code} {lot.material_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {!alreadyReversed && lot.reversible ? (
                        <TextField
                          size="small"
                          type="number"
                          value={lotDrafts[lot.allocation_id]?.produced_qty ?? ""}
                          onChange={(event) => updateLotDraft(lot.allocation_id, "produced_qty", event.target.value)}
                          inputProps={{ min: 0.0001, step: isBaskiReverseTarget ? "any" : 1 }}
                          helperText={operationUnit(reverseTarget?.operation_code)}
                          sx={{ width: 130 }}
                        />
                      ) : <><b>{Number(lot.produced_qty).toLocaleString("tr-TR")}</b> {operationUnit(reverseTarget?.operation_code)}</>}
                    </TableCell>
                    <TableCell align="right">
                      {!alreadyReversed && lot.reversible ? (
                        <TextField
                          size="small"
                          type="number"
                          value={lotDrafts[lot.allocation_id]?.scrap_qty ?? ""}
                          onChange={(event) => updateLotDraft(lot.allocation_id, "scrap_qty", event.target.value)}
                          inputProps={{ min: 0, step: isBaskiReverseTarget ? "any" : 1 }}
                          disabled={!scrapEnabled}
                          helperText={lotScrapUnit(reverseTarget?.operation_code)}
                          sx={{ width: 130 }}
                        />
                      ) : <b>{Number(lot.scrap_qty || 0).toLocaleString("tr-TR")}</b>}
                    </TableCell>
                    <TableCell>
                      {alreadyReversed
                        ? <Chip size="small" label="Ters alındı" />
                        : lot.reversible ? <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" disabled={reverseBusy || !reverseReason.trim()} onClick={() => submitLotCorrection(lot)}>Düzenle</Button>
                          <Button size="small" variant="outlined" color="error" disabled={reverseBusy || !reverseReason.trim()} onClick={() => (isBaskiReverseTarget ? submitBaskiLotReversal(lot) : submitLotReversal(lot))}>Parti Onayını Geri Al</Button>
                        </Stack>
                        : <Chip size="small" color="warning" label="Parti bazında alınamaz" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseTarget(null)} disabled={reverseBusy} color="inherit">Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={operationChoices.length > 0} onClose={() => setOperationChoices([])} fullWidth maxWidth="sm"><DialogTitle>Operasyon seçin</DialogTitle><DialogContent dividers><Stack spacing={1}>{operationChoices.map((op) => <Button key={op.routing_operation_id} variant="outlined" onClick={() => openEntry(selectedOrder, op)} sx={{ justifyContent: "flex-start" }}>OP {op.op_no} · {op.operation_code} — {op.description}</Button>)}</Stack></DialogContent></Dialog>

      <Dialog open={dialogOpen} onClose={formLoading ? undefined : () => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Vardiya üretimi — #{selectedOrder?.order_no} · OP {selectedOperation?.op_no} {selectedOperation?.operation_code}</DialogTitle>
        <DialogContent dividers><Stack spacing={2}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <Alert severity="info">{selectedCenter?.name} · Hedef {Number(selectedOperation?.qty_target || 0).toLocaleString("tr-TR")} · Yapılan {Number(selectedOperation?.qty_done || 0).toLocaleString("tr-TR")}</Alert>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField required type="date" label="Üretim tarihi" InputLabelProps={{ shrink: true }} value={form.production_date} onChange={(e) => setForm((p) => ({ ...p, production_date: e.target.value }))} />
            <FormControl required sx={{ minWidth: 180 }}><InputLabel>Vardiya</InputLabel><Select label="Vardiya" value={form.shift_id} onChange={(e) => setForm((p) => ({ ...p, shift_id: e.target.value }))}>{shifts.map((s) => <MenuItem key={s.id} value={s.id}>{s.shift_no}. Vardiya ({s.start_time}–{s.end_time})</MenuItem>)}</Select></FormControl>
            <FormControl required fullWidth><InputLabel>Operatörler</InputLabel><Select multiple label="Operatörler" value={form.operator_user_ids} onChange={(e) => setForm((p) => ({ ...p, operator_user_ids: e.target.value }))} renderValue={(ids) => ids.map((id) => operators.find((op) => Number(op.id) === Number(id))?.name).filter(Boolean).join(", ")}>{operators.map((op) => <MenuItem key={op.id} value={op.id}>{op.name}{op.employee_code ? ` (${op.employee_code})` : ""}</MenuItem>)}</Select></FormControl>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
            <Alert severity="info" sx={{ flex: 1 }}>Toplam üretim: <b>{productionRows.reduce((sum, row) => sum + (Number(row.qty) > 0 ? Number(row.qty) : 0), 0).toLocaleString("tr-TR")}</b> {operationUnit(selectedOperation?.operation_code)}{(/bask|print|kesim|cut|frm|form|shrk|shrink|şirink/i.test(opCode)) ? <> · Toplam fire: <b>{productionRows.reduce((sum, row) => sum + (Number(row.scrapQty) >= 0 ? Number(row.scrapQty) : 0), 0).toLocaleString("tr-TR")}</b> {/kesim|cut/i.test(opCode) ? "tabaka" : /bask|print/i.test(opCode) ? "metre" : "adet"}</> : null} (yalnız ana girdi satırlarının toplamı)</Alert>
            <Button variant="outlined" onClick={() => loadRequirements()} disabled={formLoading}>Malzeme/partileri yenile</Button>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <TextField required type="number" label={`Makine hızı (${machineSpeedUnit(opCode)})`} value={form.machine_speed} onChange={(e) => setForm((p) => ({ ...p, machine_speed: e.target.value }))} inputProps={{ min: 0.0001, step: "any" }} />
          </Stack>
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Box>
                <Typography fontWeight={800}>Duruşlar</Typography>
                <Typography variant="body2" color="text.secondary">Arıza dışı duruşları dakika olarak girin.</Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={addDowntime} disabled={!downtimeReasons.length}>+ Duruş ekle</Button>
            </Stack>
            {!downtimeReasons.length ? <Alert severity="info">Aktif duruş sebebi tanımlanmamış. Kartlar sekmesinden ekleyebilirsiniz.</Alert> : null}
            <Stack spacing={1}>
              {(form.downtimes || []).map((row, index) => <Stack key={index} direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                <Typography width={30} color="text.secondary">{index + 1}</Typography>
                <FormControl size="small" fullWidth required><InputLabel>Duruş sebebi</InputLabel><Select label="Duruş sebebi" value={row.reason_id} onChange={(e) => updateDowntime(index, { reason_id: e.target.value })}><MenuItem value=""><em>Seçin</em></MenuItem>{downtimeReasons.map((reason) => <MenuItem key={reason.id} value={reason.id}>{reason.code} — {reason.name}</MenuItem>)}</Select></FormControl>
                <TextField required size="small" type="number" label="Süre (dakika)" value={row.duration_minutes} onChange={(e) => updateDowntime(index, { duration_minutes: e.target.value })} inputProps={{ min: 0.0001, step: "any" }} sx={{ width: { xs: "100%", md: 210 } }} />
                <Button color="error" onClick={() => removeDowntime(index)}>Sil</Button>
              </Stack>)}
            </Stack>
          </Box>
          <TextField multiline minRows={2} label="Not" value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />
          {primaryInputError ? <Alert severity="error">{primaryInputError}</Alert> : null}
          <Box><Typography fontWeight={800}>Ana girdi / üretim partileri — 20 satır</Typography><Typography variant="body2" color="text.secondary" mb={1}>{/bask|print/i.test(opCode) ? "Her bobin partisi için üretilen metreyi ve fireyi yazın; stoktan yalnızca bu ikisinin toplamı düşer." : /kesim|cut/i.test(opCode) ? "Her bobin partisi için iyi tabaka ve fire tabaka miktarını yazın; ikisi de bobin tüketimine girer, yalnız iyi tabaka çıktıya alınır." : /frm|form/i.test(opCode) ? "Her parti için üretilen sağlam adedi ve fire adedini yazın. Stoktan sağlam + fire düşer, çıktıya yalnız sağlam adet alınır." : /shrk|shrink|şirink/i.test(opCode) ? "Her parti için üretilen sağlam paket sayısını ve fire adedini yazın. Ana girdiden sağlam paket × paket içi adet + fire düşer, çıktıya yalnız sağlam paket alınır." : "Bobin, kâğıt veya önceki operasyondan gelen yarı mamul partisini seçip bu partiden üretilen miktarı yazın."}</Typography>
            <Stack spacing={1}>{productionRows.map((row, index) => <Stack key={index} direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
              <Typography width={30} color="text.secondary">{index + 1}</Typography>
              <FormControl size="small" fullWidth><InputLabel>Ana girdi + parti</InputLabel><Select label="Ana girdi + parti" value={row.optionKey} onChange={(e) => updateProduction(index, { optionKey: e.target.value })}><MenuItem value=""><em>Boş</em></MenuItem>{primaryLotOptions.map((option) => <MenuItem key={option.key} value={option.key} disabled={productionRows.some((other, otherIndex) => otherIndex !== index && other.optionKey === option.key)}>{option.label}</MenuItem>)}</Select></FormControl>
              <TextField size="small" type="number" label={`Üretilen miktar (${operationUnit(selectedOperation?.operation_code)})`} value={row.qty} onChange={(e) => updateProduction(index, { qty: e.target.value })} inputProps={{ min: 0.0001, step: "any" }} sx={{ width: { xs: "100%", md: 240 } }} />
              {/bask|print|kesim|cut|frm|form|shrk|shrink|şirink/i.test(opCode) ? <TextField required size="small" type="number" label={/kesim|cut/i.test(opCode) ? "Fire (tabaka)" : /bask|print/i.test(opCode) ? "Fire (metre)" : "Fire (adet)"} value={row.scrapQty} onChange={(e) => updateProduction(index, { scrapQty: e.target.value })} inputProps={{ min: 0, step: /bask|print/i.test(opCode) ? "any" : 1 }} sx={{ width: { xs: "100%", md: 180 } }} /> : null}
            </Stack>)}</Stack>
          </Box>
          <Box><Typography fontWeight={800}>Yardımcı malzeme tüketim partileri</Typography><Typography variant="body2" color="text.secondary" mb={1}>Boya, lak, etiket, koli ve shrink için yalnızca stok partisini seçin. Tüketim miktarı toplam üretime göre otomatik hesaplanır. İhtiyaç: {auxiliaryRequirements.map((r) => `${r.material_code || r.material_id}: ${Number(r.qty || 0).toLocaleString("tr-TR")} ${r.uom || ""}`).join(" · ") || "Yardımcı tüketim yok"}</Typography>
            {auxiliaryRequirements.length ? <Stack spacing={1}>{consumptionRows.map((row, index) => <Stack key={index} direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
              <Typography width={30} color="text.secondary">{index + 1}</Typography>
              <FormControl size="small" fullWidth><InputLabel>Yardımcı malzeme + parti</InputLabel><Select label="Yardımcı malzeme + parti" value={row.optionKey} onChange={(e) => updateConsumption(index, { optionKey: e.target.value })}><MenuItem value=""><em>Boş</em></MenuItem>{auxiliaryLotOptions.map((option) => <MenuItem key={option.key} value={option.key} disabled={consumptionRows.some((other, otherIndex) => otherIndex !== index && other.optionKey === option.key)}>{option.label}</MenuItem>)}</Select></FormControl>
            </Stack>)}</Stack> : <Alert severity="info">Bu operasyon için yardımcı malzeme tüketimi bulunmuyor.</Alert>}
          </Box>
        </Stack></DialogContent>
        <DialogActions>
          <Button color="error" variant="outlined" onClick={completeSelectedOperation} disabled={formLoading}>Operasyon komple bitti</Button>
          <Button onClick={() => setDialogOpen(false)} disabled={formLoading}>İptal</Button>
          <Button variant="contained" onClick={submitEntry} disabled={formLoading}>{formLoading ? "Kaydediliyor…" : "Vardiya Kaydını Oluştur"}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
